import React, { useMemo, useCallback } from 'react';
import { Text, Line } from 'react-konva';
import { differenceInDays, startOfDay, endOfDay } from 'date-fns';
import { Bar, RateOverride } from './schedulerTypes';
import { BarResizer } from './BarResizer';
import { darken } from 'polished';
import { BarShape } from './BarShape';

interface SchedulerBarProps {
  bar: Bar;
  schedulerStartDate: Date;
  dayWidth: number;
  trackHeight: number;
  isSelected: boolean;
  onSelect: () => void;
  onChange: (updatedBar: Bar) => void;
  onResizeComplete: (updatedBar: Bar) => void;
  onBarTextClick?: (bar: Bar) => void;
  verticalOffset?: number;
}

export const SchedulerBar: React.FC<SchedulerBarProps> = React.memo(function SchedulerBar({
  bar,
  schedulerStartDate,
  dayWidth,
  trackHeight,
  isSelected,
  onSelect,
  onChange,
  onResizeComplete,
  onBarTextClick,
  verticalOffset = 0
}: SchedulerBarProps) {
  const daysFromStart = useMemo(() => 
    differenceInDays(bar.startDate, schedulerStartDate),
    [bar.startDate, schedulerStartDate]
  );
  
  const totalDays = useMemo(() => 
    Math.max(differenceInDays(bar.endDate, bar.startDate) + 1, 1),
    [bar.startDate, bar.endDate]
  );

  const defaultRate = useMemo(() => {
    return typeof bar.assignmentRate === 'number' ? bar.assignmentRate : 1;
  }, [bar.assignmentRate]);

  const getDefaultHeight = useMemo(() => {
    return (date: Date) => {
      const dayOfWeek = date.getDay(); // 0 = Sunday, 6 = Saturday
      return dayOfWeek === 0 || dayOfWeek === 6 ? 0 : (trackHeight - 10);
    };
  }, [trackHeight]);

  const getHeightForDay = useMemo(() => {
    return (index: number) => {
      const currentDate = new Date(schedulerStartDate);
      currentDate.setDate(currentDate.getDate() + daysFromStart + index);
      
      // Check for rate overrides on weekends
      const dayOfWeek = currentDate.getDay(); // 0 = Sunday, 6 = Saturday
      if (dayOfWeek === 0 || dayOfWeek === 6) {
        // Check if there's a rate override for this day
        if (bar.rateOverrides && bar.rateOverrides.length > 0) {
          currentDate.setHours(0, 0, 0, 0);
          
          for (const o of bar.rateOverrides) {
            const overrideStartDate = new Date(o.startDate);
            const overrideEndDate = new Date(o.endDate);
            
            overrideStartDate.setHours(0, 0, 0, 0);
            overrideEndDate.setHours(0, 0, 0, 0);
            
            if (currentDate >= overrideStartDate && currentDate <= overrideEndDate) {
              // If we have a rate override on a weekend, use normal height instead of 0
              return trackHeight - 10;
            }
          }
        }
        return 0; // Weekend with no override
      }
      
      // Weekday
      return trackHeight - 10;
    };
  }, [schedulerStartDate, daysFromStart, trackHeight, bar.rateOverrides]);

  const getRateForDay = useCallback((dayIndex: number) => {
    if (!bar.rateOverrides || bar.rateOverrides.length === 0) {
      return defaultRate;
    }
    
    // Get the current date for the day index we're checking
    const currentDate = new Date(schedulerStartDate);
    currentDate.setDate(currentDate.getDate() + daysFromStart + dayIndex);
    currentDate.setHours(0, 0, 0, 0); // Reset time part
    
    for (const o of bar.rateOverrides) {
      // Use the dates directly since they're already properly formatted
      // No need for timezone adjustments now
      const overrideStartDate = new Date(o.startDate);
      const overrideEndDate = new Date(o.endDate);
      
      // Reset hours to ensure date-only comparison
      overrideStartDate.setHours(0, 0, 0, 0);
      overrideEndDate.setHours(0, 0, 0, 0);
      
      // Simple date comparison at day level
      const isWithinOverride = (
        currentDate >= overrideStartDate && 
        currentDate <= overrideEndDate
      );
      
      if (isWithinOverride) {
        return o.rate;
      }
    }
    
    return defaultRate;
  }, [bar.rateOverrides, defaultRate, daysFromStart, schedulerStartDate, bar.id]);

  const handleResizeComplete = useCallback((updatedStartDate: Date, updatedEndDate: Date) => {
    const updatedBar = {
      ...bar,
      startDate: updatedStartDate,
      endDate: updatedEndDate,
    };
    onChange(updatedBar);
    onResizeComplete(updatedBar);
  }, [bar, onChange, onResizeComplete]);

  const handleTextClick = useCallback((e: any) => {
    e.cancelBubble = true;
    if (onBarTextClick) onBarTextClick(bar);
  }, [bar, onBarTextClick]);

  const getBillableStatusForSegment = useCallback((dayIndex: number) => {
    // First, check if there's an override for this day
    if (bar.rateOverrides && bar.rateOverrides.length > 0) {
      const currentDate = new Date(schedulerStartDate);
      currentDate.setDate(currentDate.getDate() + daysFromStart + dayIndex);
      currentDate.setHours(0, 0, 0, 0);
      
      for (const o of bar.rateOverrides) {
        const overrideStartDate = new Date(o.startDate);
        const overrideEndDate = new Date(o.endDate);
        
        overrideStartDate.setHours(0, 0, 0, 0);
        overrideEndDate.setHours(0, 0, 0, 0);
        
        if (currentDate >= overrideStartDate && currentDate <= overrideEndDate) {
          return o.isBillable !== false; // Return override's billable status
        }
      }
    }
    
    // If no override matches or no overrides exist, return the bar's billable status
    return bar.isBillable !== false;
  }, [bar.rateOverrides, schedulerStartDate, daysFromStart, bar.isBillable]);

  const groupedRects = useMemo(() => {
    const groups: { startIndex: number; length: number; height: number }[] = [];
    let currentHeight = getHeightForDay(0);
    let startIndex = 0;

    for (let i = 1; i < totalDays; i++) {
      const height = getHeightForDay(i);
      if (height !== currentHeight) {
        groups.push({ startIndex, length: i - startIndex, height: currentHeight });
        startIndex = i;
        currentHeight = height;
      }
    }

    groups.push({ startIndex, length: totalDays - startIndex, height: currentHeight });
    return groups;
  }, [getHeightForDay, totalDays]);

  const rateSegments = useMemo(() => {
    const segments: { startIndex: number; length: number; rate: number; isBillable: boolean }[] = [];
    
    if (totalDays <= 0) return segments;
    
    let currentRate = getRateForDay(0);
    let currentBillable = getBillableStatusForSegment(0);
    let startIndex = 0;
    
    for (let i = 1; i < totalDays; i++) {
      const rate = getRateForDay(i);
      const isBillable = getBillableStatusForSegment(i);
      
      // If either rate or billable status changes, create a new segment
      if (rate !== currentRate || isBillable !== currentBillable) {
        segments.push({ 
          startIndex, 
          length: i - startIndex, 
          rate: currentRate,
          isBillable: currentBillable
        });
        startIndex = i;
        currentRate = rate;
        currentBillable = isBillable;
      }
    }
    
    segments.push({ 
      startIndex, 
      length: totalDays - startIndex, 
      rate: currentRate,
      isBillable: currentBillable
    });
    
    
    return segments;
  }, [getRateForDay, getBillableStatusForSegment, totalDays, bar.id]);

  const colors = useMemo(() => {
    return bar.color || "#CBD5E1";
  }, [bar.color]);

  const handleMouseEnter = useCallback((e: any) => {
    const container = e.target.getStage()?.container();
    if (container) container.style.cursor = 'pointer';
    e.target.setAttr('textDecoration', 'underline');
    e.target.getLayer()?.batchDraw();
  }, []);

  const handleMouseLeave = useCallback((e: any) => {
    const container = e.target.getStage()?.container();
    if (container) container.style.cursor = 'default';
    e.target.setAttr('textDecoration', '');
    e.target.getLayer()?.batchDraw();
  }, []);

  return (
    <>
      {groupedRects.map((group, index) => {
        const x = (daysFromStart + group.startIndex) * dayWidth;
        const width = group.length * dayWidth;
        const height = group.height;
        const y = trackHeight - height - 5 + verticalOffset;

        if (height <= 0) {
          return (
            <Line
              key={`line-${index}`}
              points={[x, trackHeight / 2 + verticalOffset, x + width, trackHeight / 2 + verticalOffset]}
              stroke={darken(0.2, colors)}
              strokeWidth={1}
              onClick={onSelect}
            />
          );
        }

        const barSegments = rateSegments
          .filter(segment => {
            const rateEnd = segment.startIndex + segment.length - 1;
            const groupEnd = group.startIndex + group.length - 1;
            return !(rateEnd < group.startIndex || segment.startIndex > groupEnd);
          })
          .map(segment => {
            const overlapStart = Math.max(segment.startIndex, group.startIndex);
            const overlapEnd = Math.min(segment.startIndex + segment.length - 1, group.startIndex + group.length - 1);
            const overlapLength = overlapEnd - overlapStart + 1;
            
            const startPosition = (overlapStart - group.startIndex) * dayWidth;
            
            
            return {
              start: startPosition,
              width: overlapLength * dayWidth,
              rate: segment.rate,
              isBillable: segment.isBillable
            };
          });


        return (
          <BarShape
            key={`rect-${index}`}
            x={x}
            y={y}
            width={width}
            height={height}
            color={colors}
            segments={barSegments}
            onClick={onSelect}
          />
        );
      })}

      <BarResizer
        startDate={bar.startDate}
        endDate={bar.endDate}
        schedulerStartDate={schedulerStartDate}
        dayWidth={dayWidth}
        trackHeight={trackHeight}
        onResizeComplete={handleResizeComplete}
        verticalOffset={verticalOffset}
      />

      {groupedRects.map((group, index) => {
        const x = (daysFromStart + group.startIndex) * dayWidth;
        const width = group.length * dayWidth;
        const height = group.height;

        if (height > 0) {
          return (
            <Text
              key={`text-${index}`}
              x={x + 5}
              y={10 + verticalOffset}
              text={width >= 100 ? bar.name : bar.code}
              fontSize={12}
              fill="#000"
              width={width - 10}
              ellipsis
              onClick={handleTextClick}
              onTap={handleTextClick}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            />
          );
        }
        return null;
      })}
    </>
  );
});
